import SectionTitle from './SectionTitle'
import Entry from './Entry'
import React from 'react'

export default function Experience() {
    return (
        <section>
            <SectionTitle title="Experience" />
            <div className="mt-6 space-y-8">
                {/* Betabit */}
                <Entry
                    title="Founder & Principal Engineer"
                    dates="Dec 2018 – Present"
                    company="Betabit"
                    location="San Juan, PR"
                >
                    <p>I lead Betabit, where we engineer AI, software, and cloud solutions delivering measurable business impact—combining deep technical execution with strategic thinking.</p>

                    <p className="mt-4 font-medium text-neutral-700">📌 Focus areas:</p>
                    <ul className="mt-6 space-y-6 text-neutral-600">
                        <li>🤖 <span className="font-medium text-neutral-700">AI & Intelligent Systems:</span> Building practical AI solutions with OpenAI, Claude, and custom models. Deploying RAG, intelligent automation, and scalable data pipelines.</li>
                        <li>☁️ <span className="font-medium text-neutral-700">Cloud & Infrastructure:</span> Architecting AWS solutions (Terraform/CDK), achieving zero-downtime migrations, and reducing client costs by $30K+/year.</li>
                        <li>💻 <span className="font-medium text-neutral-700">Full-Stack Development:</span> Daily production code (React, TypeScript, Python, Java), building complete, deployable software—not just theoretical designs.</li>
                        <li>🚀 <span className="font-medium text-neutral-700">Technical Leadership:</span> Providing fractional CTO services, team transformations, and technical architecture reviews. Managed teams of 20+ engineers, delivering complex initiatives for Paylocity, Augment Risk, Allied World, and Attune Insurance.</li>
                    </ul>

                    <p className="mt-3 text-neutral-600">💡 I cut through hype to deliver solutions that actually scale—because shipping beats strategy every time.</p>

                    <p className="mt-6 font-medium text-neutral-700">✨ Key Projects:</p>
                    <ul className="mt-4 space-y-6 text-neutral-600">
                        <li>🔹 <span className="font-medium text-neutral-700">Augment Risk – Lead AI & Data Consultant (2025)</span><br/>Built Iceberg/Nessie data lake and Airflow pipelines in &lt;1 month, with GUI data apps, to be leveraged in an upcoming in-house AI platform.</li>
                        <li>🔹 <span className="font-medium text-neutral-700">Paylocity (Infrastructure Consultant)</span><br/>Migrated AWS infrastructure (ECS EC2 → Fargate), zero downtime, cutting costs 25%.</li>
                        <li>🔹 <span className="font-medium text-neutral-700">Allied World (Data Solutions Lead)</span><br/>Delivered centralized ETL platform ("Harvester") with Airflow & Windmill; reduced data processing from months to days.</li>
                        <li>🔹 <span className="font-medium text-neutral-700">Attune Insurance (Head of DevOps)</span><br/>Migrated to AWS Fargate, saving $1.2M annually; built personalized dev/test environments, boosting productivity.</li>
                    </ul>
                </Entry>

                {/* Augment Risk */}
                <Entry
                    title="Lead AI & Data Consultant — Contract"
                    dates="Jun 2025 – Present"
                    company="Augment Risk"
                    location="Remote"
                >
                    <ul className="space-y-6 text-neutral-600">
                        <li>🚀 Centralized data platform on Windmill—scalable control plane for ingestion, processing, and orchestration.</li>
                        <li>🧠 Data Manager: AI-assisted workflow system accelerating client data onboarding from months to weeks.</li>
                        <li>⚡ AI integrated into data workflows for understanding, cleanup, and transformation—human oversight preserved.</li>
                        <li>☁️ Full platform ownership: Terraform IaC, Windmill deployment, SFTP integrations, Python data processing.</li>
                        <li>🤖 Leveraging Claude Code and custom AI workflows to accelerate design and implementation.</li>
                    </ul>
                </Entry>

                {/* Paylocity */}
                <Entry
                    title="Cloud, Software, & Data Consultant — Contract"
                    dates="Feb 2024 – Mar 2025"
                    company="Paylocity"
                    location="Remote"
                >
                    <ul className="space-y-6 text-neutral-600">
                        <li>📦 Successfully executed a zero-downtime AWS infrastructure migration (ECS EC2 → ECS Fargate), achieving a 25% reduction in cloud costs (~$2,500/month).</li>
                        <li>🛠️ Leveraged Terraform and AWS CDK to fully automate infrastructure provisioning and optimize databases.</li>
                        <li>💻 Developed AWS Lambda application logic (TypeScript/Python), demonstrating direct software engineering beyond infrastructure.</li>
                    </ul>
                </Entry>

                {/* Allied World */}
                <Entry
                    title="AVP, Platform Lead, Data Solutions — Contract"
                    dates="Feb 2022 – Dec 2023"
                    company="Allied World"
                    location="Remote"
                >
                    <ul className="space-y-6 text-neutral-600">
                        <li>🏗️ Architected and implemented "Harvester," a centralized data and ETL platform utilizing Airflow, Airbyte, and Windmill.</li>
                        <li>⏱️ Streamlined data-reporting processes, reducing timelines from months to mere days.</li>
                        <li>👥 Led and mentored the data engineering team, established best practices for security, cloud operations, and development efficiency.</li>
                    </ul>
                </Entry>

                {/* Attune Insurance */}
                <Entry
                    title="Senior Engineering Manager — Contract"
                    dates="Sept 2019 – Feb 2022"
                    company="Attune Insurance"
                    location="Remote"
                >
                    <ul className="space-y-6 text-neutral-600">
                        <li>🎯 Managed multiple cross-functional engineering teams, emphasizing performance management, career growth, mentorship, and fostering a high-performing engineering culture.</li>
                        <li>🚀 Led Engineering Managers, aligning overall management strategies and best practices company-wide.</li>
                        <li>📈 Oversaw engineering recruitment, training, performance evaluations, and continuous improvement initiatives across teams.</li>
                    </ul>
                </Entry>

                <Entry
                    title="Head of DevSecOps — Contract"
                    dates="Dec 2018 – Feb 2022"
                    company="Attune Insurance"
                    location="Remote"
                >
                    <ul className="space-y-6 text-neutral-600">
                        <li>🌩️ Directed migration from legacy third-party servers to AWS ECS Fargate, reducing infrastructure costs from $1.2M to ~$400K annually.</li>
                        <li>🛠️ Implemented full-stack Infrastructure-as-Code (Terraform) for all AWS infrastructure, including IAM, networking, and security permissions.</li>
                        <li>🧪 Built "On-Demand Sandboxes," allowing employees instant creation of personalized, isolated production-like environments via a simple UI.</li>
                        <li>🔐 Owned company-wide Security Initiatives, ensuring compliance, auditing, and technical security standards.</li>
                        <li>📊 Implemented comprehensive logging, monitoring, and alerting infrastructure for all company services, significantly improving incident detection and resolution times.</li>
                    </ul>
                </Entry>

                {/* Intent */}
                <Entry
                    title="People Manager"
                    dates="Jan 2017 – Dec 2018"
                    company="Intent Media"
                    location="New York, NY"
                >
                    <ul className="space-y-6 text-neutral-600">
                        <li>👥 Managed and mentored junior engineers, overseeing career progression, training, and professional growth.</li>
                    </ul>
                </Entry>

                <Entry
                    title="DevOps & Data Engineer"
                    dates="Jan 2016 – Dec 2018"
                    company="Intent Media"
                    location="New York, NY"
                >
                    <ul className="space-y-6 text-neutral-600">
                        <li>⚙️ Led DevOps initiatives for the Data Engineering team, streamlining deployment and infrastructure processes.</li>
                        <li>📈 Served as Delivery Lead for Backend Team, ensuring milestones and project goals were consistently met.</li>
                        <li>💡 Developed automated ML-model training pipelines using EMR and Spark.</li>
                        <li>☁️ Migrated legacy systems to AWS (ECS, Lambda, S3), implemented a scalable, high-traffic serverless API, and automated CI/CD processes.</li>
                    </ul>
                </Entry>

                <Entry
                    title="Full-Stack Engineer"
                    dates="Mar 2015 – Dec 2015"
                    company="Intent Media"
                    location="New York, NY"
                >
                    <ul className="space-y-6 text-neutral-600">
                        <li>🧪 Authored Java backend services with a strong emphasis on Test-Driven Development (TDD).</li>
                        <li>🔗 Collaborated closely across frontend and backend to ensure end-to-end feature correctness, performance, and reliability—from browser execution to server-side rendering and data flow.</li>
                        <li>🧩 Designed and built an internal lightweight JavaScript framework for rendering advertisement popups under extreme payload constraints, enabling fast load times on third-party partner sites where standard frontend frameworks were not allowed.</li>
                        <li>🧠 Conducted ongoing R&D on browser window and popup behavior across vendors, implementing adaptive techniques to maintain ad visibility despite frequent browser security and UX changes.</li>
                    </ul>
                </Entry>

                {/* Hackerati */}
                <Entry
                    title="Software Engineer"
                    dates="Jan 2015 – Sep 2015"
                    company="Hackerati"
                    location="New York, NY"
                >
                    <ul className="space-y-6 text-neutral-600">
                        <li>💻 Provided consulting on Frontend development, specializing in Angular, React, and modern JavaScript frameworks for diverse clients.</li>
                    </ul>
                </Entry>

                {/* Optivon */}
                <Entry
                    title="Software Engineer"
                    dates="Oct 2013 – Dec 2014"
                    company="Optivon"
                    location="San Juan, PR"
                >
                    <ul className="space-y-6 text-neutral-600">
                        <li>🚔 Developed foundational components for law-enforcement software suite, including Android mobile app and AWS-based web dashboard.</li>
                        <li>🌐 Participated in full lifecycle: product ideation, architecture, frontend/backend implementation, and deployment to AWS.</li>
                    </ul>
                </Entry>
            </div>
        </section>
    )
}
